import React from "react";
import { Mail, Phone, Calendar, MapPin, Users, DollarSign, RefreshCw } from "lucide-react";
import api from "../../lib/api";

const STATUS = {
  new: { label: "Nouveau", cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30" },
  contacted: { label: "Contacté", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  quoted: { label: "Devis envoyé", cls: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
  won: { label: "Gagné", cls: "bg-green-500/15 text-green-300 border-green-500/30" },
  lost: { label: "Perdu", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

export default function AdminDevis() {
  const [items, setItems] = React.useState([]);
  const [filterStatus, setFilterStatus] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const r = await api.get(`/admin/quotes${params}`);
      setItems(r.data);
    } finally { setLoading(false); }
  }, [filterStatus]);

  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    load();
  };

  const updateNotes = async (id, admin_notes) => {
    await api.patch(`/admin/quotes/${id}`, { admin_notes });
    setEditing(null);
    load();
  };

  return (
    <div className="text-text-cream" data-testid="admin-devis-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-4xl">Demandes de devis</h1>
          <p className="text-text-cream/50 mt-2 text-sm font-light">Mariages & évènements — pipeline de conversion.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
            data-testid="filter-status"
          >
            <option value="">Tous</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={load} className="text-text-cream/60 hover:text-accent-light p-2"><RefreshCw size={14}/></button>
        </div>
      </div>

      {loading && <p className="text-text-cream/40">Chargement...</p>}
      {!loading && items.length === 0 && (
        <p className="text-text-cream/40 py-10 text-center">Aucune demande pour le moment.</p>
      )}

      <div className="space-y-4">
        {items.map(q => {
          const st = STATUS[q.status] || STATUS.new;
          return (
            <div key={q.id} className="bg-white/[0.03] border border-white/10 rounded p-5" data-testid={`devis-row-${q.id}`}>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="font-serif text-xl">{q.prenom} {q.nom}</h3>
                  <div className="text-text-cream/50 text-xs mt-1">
                    {q.service_name} · reçu {new Date(q.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <select
                  value={q.status}
                  onChange={e => updateStatus(q.id, e.target.value)}
                  className={`px-2 py-1 text-[0.65rem] uppercase tracking-wider border rounded bg-dark cursor-pointer ${st.cls}`}
                  data-testid={`status-${q.id}`}
                >
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-text-cream/70 mb-4">
                <InfoLine icon={<Mail size={12}/>} value={<a href={`mailto:${q.email}`} className="text-accent-light">{q.email}</a>}/>
                <InfoLine icon={<Phone size={12}/>} value={<a href={`tel:${q.tel}`} className="text-accent-light">{q.tel}</a>}/>
                {q.event_date && <InfoLine icon={<Calendar size={12}/>} value={q.event_date}/>}
                {q.event_location && <InfoLine icon={<MapPin size={12}/>} value={q.event_location}/>}
                {q.duration_hint && <InfoLine icon={<Calendar size={12}/>} value={`Durée : ${q.duration_hint}`}/>}
                {q.guests_count && <InfoLine icon={<Users size={12}/>} value={`${q.guests_count} invités`}/>}
                {q.budget_hint && <InfoLine icon={<DollarSign size={12}/>} value={`Budget : ${q.budget_hint}`}/>}
              </div>

              {q.message && (
                <div className="p-3 bg-white/[0.02] border border-white/5 rounded text-sm text-text-cream/70 font-light leading-relaxed mb-3">
                  <div className="text-text-cream/40 text-xs mb-1">Message</div>
                  {q.message}
                </div>
              )}

              {/* Admin notes */}
              {editing === q.id ? (
                <div>
                  <textarea
                    rows={2}
                    defaultValue={q.admin_notes || ""}
                    onBlur={e => updateNotes(q.id, e.target.value)}
                    autoFocus
                    className="w-full bg-white/5 border border-accent-deep/50 rounded px-3 py-2 text-sm"
                    data-testid={`notes-input-${q.id}`}
                  />
                </div>
              ) : (
                <div onClick={() => setEditing(q.id)} className="text-xs text-text-cream/50 italic cursor-pointer hover:text-accent-light">
                  {q.admin_notes || "+ ajouter une note interne"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InfoLine({ icon, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-accent-light/60">{icon}</span>
      <span>{value}</span>
    </div>
  );
}
